
import React from 'react';
import { FilterOption } from '@/components/ui/FilterDropdown';
import { StandardSelect } from '@/components/ui/form-fields';
import { CloudSun, Palette } from 'lucide-react';
import styles from '../FilterBar.module.css';
import { motion } from 'framer-motion';

interface MoodFilterProps {
  moodOptions: FilterOption[];
  onMoodChange: (options: FilterOption[]) => void;
  colorOptions: FilterOption[];
  onColorChange: (options: FilterOption[]) => void;
  showMood?: boolean;
  showColors?: boolean;
}

export const MoodFilter: React.FC<MoodFilterProps> = ({
  moodOptions,
  onMoodChange,
  colorOptions,
  onColorChange,
  showMood = true,
  showColors = true
}) => {
  // Handle mood select change - no clearing logic needed
  const handleMoodChange = (selected: string | string[]) => {
    const value = typeof selected === 'string' ? selected : '';
    
    const updatedOptions = moodOptions.map(option => ({
      ...option,
      checked: option.id === value
    }));
    onMoodChange(updatedOptions);
  };

  // Handle color select change - no clearing logic needed
  const handleColorChange = (selected: string | string[]) => {
    const value = typeof selected === 'string' ? selected : '';
    
    const updatedOptions = colorOptions.map(option => ({
      ...option,
      checked: option.id === value
    }));
    onColorChange(updatedOptions);
  };

  // Get selected mood option
  const selectedMood = moodOptions.find(option => option.checked)?.id || '';
  
  // Get selected color option
  const selectedColor = colorOptions.find(option => option.checked)?.id || '';
  
  // Helper to convert FilterOptions to StandardSelect options format
  const mapToSelectOptions = (options: FilterOption[]) => {
    return options.map(option => ({
      value: option.id,
      label: option.label
    }));
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      {showMood && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <StandardSelect
            id="mood"
            label="Mood"
            icon={<CloudSun size={16} />}
            colorVariant="red"
            value={selectedMood}
            isMulti={false}
            onChange={handleMoodChange}
            options={[
              { value: 'none', label: 'None' },
              ...mapToSelectOptions(moodOptions)
            ]}
            placeholder="Mood"
            className={styles.vibrantInput}
          />
        </motion.div>
      )}
      
      {showColors && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <StandardSelect
            id="color"
            label="Color Palette"
            icon={<Palette size={16} />}
            colorVariant="red"
            value={selectedColor}
            isMulti={false}
            onChange={handleColorChange}
            options={[
              { value: 'none', label: 'None' },
              ...mapToSelectOptions(colorOptions)
            ]}
            placeholder="Colors"
            className={styles.vibrantInput}
          />
        </motion.div>
      )}
    </div>
  );
};
